import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Spin, notification } from 'antd';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import RichSnippetWriter from '../../articles/RichSnippetWriter';
import ListArticles from '../../articles/ListArticles';
import { refreshListArticle } from '../../../service/memory/recoil/listarticle';
import StrapiService from '../../articles/StrapiService';
import "./journal.css"
import { TextField } from '@mui/material';
import BackButton from '../../generic/elements/backbutton/BackButton';
import { strings } from '../../utils/strings';
import { notifiyUser } from '../../utils/Notification';
import ConfigurationConstants from '../../utils/configurationconstants';
import { currentConfiguration } from '../../../service/memory/recoil/configurationstore';
import SelectPatientName from '../../carecoordinator/requestforvolunteeringservice/components/SelectPatientName';
import { useReadMemory } from '../../generic/hooks/useReadMemory';
import MemoryType from '../../../service/memory/enum/MemoryType';
import { useGetOrgIdentifier } from '../../generic/hooks/useGetOrgIdentifier';
import EditArticle from './EditArticle';

const Journal = () => {
    const strapiService = new StrapiService();
    const [topic, setTopic] = useState("");
    const [receivedStrapiData, setReceivedStrapiData] = useState({});
    const [loading, setLoading] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
    const setRefreshArticle = useSetRecoilState(refreshListArticle);
    const configuration = useRecoilValue(currentConfiguration);
    const [actionIcons, setActionIcons] = useState([])
    const [patientName, setPatientName] = useState([]);
    const [showPatientSelect, setShowPatientSelect] = useState(false);
    const userIdentifier = useReadMemory(MemoryType.USER)?.userIdentifier;
    const organizationIdentifier = useGetOrgIdentifier()
    const [modalOpen, setModalOpen] = useState(false);
    const [journalId, setJournalId] = useState(null)

    const handleJournalDelete = async (id) => {
        try {
            const response = await strapiService.deleteBlog(id);
            notifiyUser(response, strings.DELETE_JOURNAL_SUCCESS, strings.DELETE_JOURNAL_ERROR)
            setRefreshArticle(preValue => !preValue)
        } catch (error) {
            notification.error({
                message: "Error while deleting Announcement",
                placement: "bottomRight",
            });
        }
    }

    const handleJournalEdit = async (id) => {
        setModalOpen(true)
        setJournalId(id)
    }

    useEffect(() => {
        const configurationJournalDelete = configuration[ConfigurationConstants.FEATUREACCESS_JOURNAL_DELETE]
        const isJournalDeleteAllowed = configurationJournalDelete?.visibility;
        if (isJournalDeleteAllowed) {
            setActionIcons([
                {
                    label: 'Update',
                    icon: 'ARTICLE_EDIT_ICON',
                    map: 'ARTICLE_EDIT_ICON',
                    functionName: (post) => {
                        handleJournalEdit(post?.id);
                    },
                },
                {
                    label: 'Delete',
                    icon: 'ARTICLE_DELETE_ICON',
                    map: 'ARTICLE_DELETE_ICON',
                    functionName: (post) => {
                        handleJournalDelete(post?.id);
                    },
                },
            ])
        }

        const configurationPatientSelect = configuration[ConfigurationConstants.FEATUREACCESS_JOURNAL_PATIENT_SELECT]
        setShowPatientSelect(configurationPatientSelect?.visibility);
    }, [configuration])
        
    const handleOnChange = (e) => {
        const inputValue = e.target.value;
        setTopic(inputValue);
        setReceivedStrapiData(prevData => ({ ...prevData, topic: inputValue }));
        const isContentEmpty = receivedStrapiData?.body?.trim() === "";
        setIsSubmitDisabled(inputValue === "" || isContentEmpty);
    };

    const handleStrapiDataChange = (data) => {
        const modifiedData = {
            ...data,
            category: "Journal",
            organizationIdentifier,
            topic: topic,
        };
        setReceivedStrapiData(modifiedData);
        const isInputEmpty = topic?.trim() === "";
        if (data.body === "<p><br></p>") {
            setIsSubmitDisabled(true);
        } else {
            setIsSubmitDisabled(isInputEmpty || !data?.body?.trim());
        }
    };

    const uploadBlog = async () => {
      try {
        setLoading(true);
        const res = await strapiService.uploadBlog(receivedStrapiData);
        if (res.status === 200) {
          setTopic("");
          setReceivedStrapiData({});
          setResetKey((prevKey) => prevKey + 1);
          setPatientName([]);
          setRefreshArticle((preValue) => !preValue);
        } else {
          notification.error({
            message: "Something went wrong. Please try again.",
            placement: "bottomRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Something went wrong. Please try again.",
          placement: "bottomRight",
        });
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
        const patient = patientName?.value && JSON.parse(patientName?.value)
        setReceivedStrapiData({
            ...receivedStrapiData, patientIdentifier: patient?.userIdentifier, patientFamilyName: patient?.familyName, patientGivenName: patient?.givenName
        })
    }, [patientName])

    return (
        <>
            <section className="section-content">
                <div className="form-renderer-back-button">
                    <BackButton />
                    <p>Journal</p>
                </div>
                <Row>
                    <Col xs={24} md={8}>
                        <div className='journal-section'>
                            <div className='journal-header-section'>
                                <label htmlFor="topicInput" className='required-mark-label'>Topic</label>
                                <TextField fullWidth
                                    label='Journal Topic'
                                    type="text"
                                    id="topicInput"
                                    value={topic}
                                    onChange={handleOnChange}
                                />
                            </div>
                            <div className='journal-richsnippet-section'>
                                <RichSnippetWriter key={resetKey} onStrapiDataChange={handleStrapiDataChange} />
                            </div>
                            {showPatientSelect && (
                                <div className='journal-patient-selector-section'>
                                    <SelectPatientName patientName={patientName} setPatientName={setPatientName} showCancel={true} />
                                </div>
                            )}
                            <div className={showPatientSelect ? 'journal-button' : 'journal-button journal-button-section'}>
                                {loading ? (
                                    <Spin size="small" className="small-spinner" />
                                ) : (
                                    <Button
                                        type="primary"
                                        className="submit-button"
                                        onClick={uploadBlog}
                                        disabled={isSubmitDisabled}>
                                        Submit
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={16}>
                        <div className="scrollable-container">
                            <ListArticles type={"Journal"} actions={{ actionIcons }} userIdentifier={userIdentifier} organizationIdentifier={organizationIdentifier}/>
                        </div>
                    </Col>
                </Row>
                {
                    modalOpen && <EditArticle articleId={journalId} type={"Journal"} modalOpen={modalOpen} setModalOpen={setModalOpen} />
                }
            </section>
        </>
    );
}

export default Journal;